import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { LiveVehicle } from '@/features/live/types';

/**
 * D3 — canvas 1i's "Search vehicle, plate, driver…" box, as a REAL control.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS SHIPS AS A FILTER AND NOT AS A SEARCH FEATURE.
 * ─────────────────────────────────────────────────────────────────────────────
 * There is no search endpoint and no index, and building one was not this slice.
 * But a rendered-but-dead box is banned outright — a control that does nothing is
 * worse than an absent one, because it promises a capability and then fails
 * silently in the user's hands. And omitting the box breaks the fidelity the
 * canvas asks for.
 *
 * So it filters the vehicles THE SCREEN HAS ALREADY LOADED, in memory, and jumps
 * to `/vehicles/:id`. No endpoint, no round trip, no new query. Genuinely useful
 * at 42 vehicles, honest at one.
 *
 * 🔴 "DRIVER" IS DELIBERATELY NOT IN THE PLACEHOLDER, though the canvas says it.
 * There is no driver model in this product yet, so a box offering to search
 * drivers would be exactly the promise this file exists to avoid — the canvas
 * describing a fleet we do not yet have. Plate and name are what we hold, so
 * plate and name are what it says.
 *
 * MATCHING is case-insensitive and ignores spaces in the plate, because "KN654"
 * and "KN 654" are the same vehicle to everyone except a string comparison.
 */

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, '');

export function FleetSearch({ vehicles, t }: { vehicles: LiveVehicle[]; t: TFunction }) {
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const matches = useMemo(() => {
    const needle = norm(q);
    if (needle.length === 0) return [];
    return vehicles
      .filter((v) => norm(v.plate).includes(needle) || norm(v.label ?? '').includes(needle))
      .slice(0, 6);
  }, [q, vehicles]);

  return (
    <div className="relative w-full sm:max-w-xs" data-testid="fleet-search">
      <label className="sr-only" htmlFor="fleet-search-input">
        {t('dashboard.search.label')}
      </label>
      <input
        id="fleet-search-input"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('dashboard.search.placeholder')}
        className="min-h-11 w-full rounded-xl border border-raad-border bg-raad-panel px-3.5 text-sm text-raad-text placeholder:text-raad-dim focus-visible:outline focus-visible:outline-2 focus-visible:outline-raad-accent"
      />
      {q.trim().length > 0 ? (
        <div
          data-testid="fleet-search-results"
          className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-raad-border bg-raad-panel shadow-lg"
        >
          {matches.length === 0 ? (
            // An honest empty result — it says nothing matched, not that nothing exists.
            <p className="px-3.5 py-3 text-xs text-raad-muted">{t('dashboard.search.noMatch')}</p>
          ) : (
            <ul>
              {matches.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/vehicles/${v.id}`)}
                    className="flex w-full items-baseline justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-raad-fill-4"
                  >
                    <span className="truncate font-display text-sm font-bold text-raad-text">{v.plate}</span>
                    {v.label ? <span className="truncate text-[11px] text-raad-dim">{v.label}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
